import { Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { LoggerService } from '@services/logger.service';
import { FormsService } from 'src/app/core/services/forms.service';

import { GeneralDynamicFormComponent } from '../../../shared/components/general-dynamic-form/general-dynamic-form.component';

import { NomLanguages } from 'src/app/core/constants';
import { Workspace } from 'src/app/core/models/core.models';
import { FormMode, FormProperty } from 'src/app/core/models/form.models';

import { AppState } from 'src/app/core/state/app.state';
import { tap } from 'rxjs';
import { DynamicFormComponent } from '../../../shared/components/dynamic-form/dynamic-form.component';

@Component({
  selector: 'app-workspace-settings',
  templateUrl: './workspace-settings.component.html',
  styleUrls: ['./workspace-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class WorkspaceSettingsComponent extends GeneralDynamicFormComponent {
  @Input() workspace: Workspace;
  @Output() componentDataChange = new EventEmitter<{
    type: string;
    value: any;
  }>();

  constructor(
    protected store: Store<AppState>,
    protected formService: FormsService,
    private translateService: TranslateService,
    protected logger: LoggerService,
  ) {
    super(formService, store, logger);
  }

  public activeFormProperties: FormProperty[] = [];
  public formProperties: FormProperty[] = [
    {
      propertyName: 'language_picker',
      controlType: 'language-picker',
    },
    {
      propertyName: 'title',
      propertyNameLanguages: {
        [NomLanguages.DEFAULT]: 'title',
        [NomLanguages.BULGARIAN]:
          'title_i18n.language_id_' + NomLanguages.BULGARIAN,
        [NomLanguages.ENGLISH]:
          'title_i18n.language_id_' + NomLanguages.ENGLISH,
        [NomLanguages.ARABIC]: 'title_i18n.language_id_' + NomLanguages.ARABIC,
      },
      propertyLabel: this.translateService.instant(
        'workspace-settings.label.workspace-title',
      ),
      controlType: 'text',
      validators: [Validators.required],
      additionalConfig: {
        placeholder: this.translateService.instant(
          'workspace-settings.placeholder.workspace-title',
        ),
      },
    },
    {
      propertyName: 'description',
      propertyLabel: this.translateService.instant(
        'workspace-settings.label.workspace-description',
      ),
      controlType: 'textarea',
      additionalConfig: {
        placeholder: this.translateService.instant(
          'workspace-settings.placeholder.workspace-description',
        ),
        rows: 4,
      },
    },
    {
      // wire key WorkspacePATCH binds as $imageUrl (base64 -> ClientImageStorage);
      // also keys the defaultImageUrl mapping in ngOnInit
      propertyName: 'image_url',
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.image',
      ),
      controlType: 'image-cropper',
      availableInFormModes: [FormMode.Edit],
      additionalConfig: {
        height: 64,
        width: 64,
        defaultImageUrl: '/assets/img/folder.png',
        containerStyleProperties: {
          width: '3rem',
        },
      },
    },
  ];

  ngOnInit() {
    this.formMode = FormMode.Create;

    if (this.workspace) {
      this.formMode = FormMode.Edit;
    }
    super.ngOnInit();
    if (this.workspace) {
      this.formMode = FormMode.Edit;
      // fix api input/output property name mess
      const inputWorkpace = {
        ...this.workspace,
        ['title_i18n.language_id_' + NomLanguages.ENGLISH]:
          this.workspace['title_i18n']?.[NomLanguages.ENGLISH],
        ['title_i18n.language_id_' + NomLanguages.BULGARIAN]:
          this.workspace['title_i18n']?.[NomLanguages.BULGARIAN],
        ['title_i18n.language_id_' + NomLanguages.ARABIC]:
          this.workspace['title_i18n']?.[NomLanguages.ARABIC],
      };

      this.formProperties = this.formProperties.map((prop) => {
        if (prop.propertyName !== 'image_url') return prop;

        const defaultImageUrl =
          this.workspace.thumbnail?.length > 0
            ? this.workspace.thumbnail
            : '/assets/img/workspace.png';

        return {
          ...prop,
          additionalConfig: {
            ...prop.additionalConfig,
            defaultImageUrl,
          },
        };
      });

      this.formService.populateFormValues(this.formGroup, inputWorkpace);

      // console.log(
      //   '%c formGroup -->> ',
      //   'color: purple; font-weight: bold;',
      //   this.formGroup,
      // );
    } else {
      this.formGroupChange.emit(this.formGroup);
    }

    // * push changes through componentDataChange -> onData "channel"
    this.formGroup
      .get('title')
      ?.valueChanges.pipe(
        tap((title) =>
          this.componentDataChange.emit({
            type: 'title',
            value: title,
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
